import { useCallback, useState, type JSX } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { isAxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { exportJizdy } from '@/api/services/jizdyService';
import type { ExportFormat } from '@/api/types/Jizda';
import { SubmitButton } from '@/components/loading';
import { firstOfMonthIsoDate, lastOfMonthIsoDate } from '@/utils/date';

const FORMAT_EXT: Record<ExportFormat, string> = { csv: 'csv', xlsx: 'xlsx' };

// Trigger a browser download for an in-memory Blob via a synthetic anchor.
// Object URL is revoked after the click so it does not leak.
const triggerBlobDownload = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

// Jízdy export (CHR-02 / ADR-0041, zadání §7 — CHR-03 reshaped into a modal).
// The toolbar inline control is gone; the trigger is now a backoffice-only
// "Export" button placed left of "Nová jízda" (OrdersPage header), reclaiming
// main-table width. Clicking opens a modal dialog owning a local Od/Do range
// (defaulting client-side to the current-month bounds) + a CSV/XLSX format
// selector + "Exportovat". On submit it fetches the file as an authenticated
// blob (so the axios bearer interceptor runs — a plain `<a href>` would 401)
// and triggers a download. Modal contents = date-range + format ONLY (no
// deleted/storno toggles — CHR-03 decision 2026-06-01). Backoffice-only: the
// parent renders this only for backoffice (the endpoint is backoffice-only).
export const OrdersExport = (): JSX.Element => {
  const { t } = useTranslation('orders');
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState<string>(firstOfMonthIsoDate);
  const [to, setTo] = useState<string>(lastOfMonthIsoDate);
  const [format, setFormat] = useState<ExportFormat>('csv');
  const [loading, setLoading] = useState(false);

  const handleExport = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const { blob, filename } = await exportJizdy(from, to, format);
      const name = filename ?? `jizdy-export_${from}_${to}.${FORMAT_EXT[format]}`;
      triggerBlobDownload(blob, name);
      enqueueSnackbar(t('export.success'), { variant: 'success' });
      setOpen(false);
    } catch (e) {
      // HTTP 4xx/5xx are already surfaced by the axios response interceptor's
      // global toast; only show our own toast for non-HTTP failures (network
      // abort, blob/anchor error) so the user is never double-toasted.
      if (!(isAxiosError(e) && e.response)) {
        enqueueSnackbar(t('export.failed'), { variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, [from, to, format, enqueueSnackbar, t]);

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FileDownloadIcon />}
        onClick={() => setOpen(true)}
        data-testid="orders-export-button"
      >
        {t('export.button')}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid="orders-export-dialog"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <span style={{ flex: 1 }}>{t('export.title')}</span>
          <IconButton onClick={() => setOpen(false)} aria-label={t('export.close')}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              <TextField
                type="date"
                autoComplete="off"
                label={t('export.from')}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  'data-testid': 'export-from',
                  'data-empty': from === '' ? 'true' : undefined,
                }}
              />
              <TextField
                type="date"
                autoComplete="off"
                label={t('export.to')}
                value={to}
                onChange={(e) => setTo(e.target.value)}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  'data-testid': 'export-to',
                  'data-empty': to === '' ? 'true' : undefined,
                }}
              />
            </Stack>
            <TextField
              select
              size="small"
              label={t('export.format')}
              value={format}
              onChange={(e) => setFormat(e.target.value as ExportFormat)}
              fullWidth
              data-testid="export-format"
            >
              <MenuItem value="csv">{t('export.formatCsv')}</MenuItem>
              <MenuItem value="xlsx">{t('export.formatXlsx')}</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpen(false)} variant="outlined">
            {t('export.close')}
          </Button>
          <SubmitButton
            variant="contained"
            startIcon={<DownloadIcon />}
            loading={loading}
            onClick={() => void handleExport()}
            data-testid="export-submit"
          >
            {t('export.submit')}
          </SubmitButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
